import React from "react";
import { Mention, MentionsInput } from "react-mentions";

import cx from "classnames";

import { coalesce } from "@kazoohr/helpers";

import { ControlWrapper } from "../ControlWrapper/ControlWrapper";

import styles from "./TextArea.scss";
import { TextAreaMentionSuggestion } from "./TextAreaMentionSuggestion";

const MAX_MENTION_SEARCH_LENGTH = 20;

export interface TextAreaProps {
  /** Identifier for this textarea */
  id: string | number;
  /** CSS class name to apply to the text area */
  className?: string;
  /** Whether or not textarea is disabled */
  disabled?: boolean;
  /** An error message and state */
  error?: string | null;
  /** Text that provides user information regarding this textarea (optional) */
  helperText?: string;
  /** Text that displays above text area (optional) */
  label?: React.ReactNode;
  /** Minimum height of the text area (set on the style prop of the div and the text area) */
  minHeight?: string;
  /** Maximum height of the text area (set on the style prop of the div and the text area) */
  maxHeight?: string;
  /** Maximum number of characters allowed */
  maxLength?: number;
  /** Minimum number of rows displayed */
  minRows?: number;
  /** Function to return suggestions after the user starts a mention */
  onMention?: (search: string, callback: (results: any) => void) => void;
  /** Function called as user types (must update state or user can't type) */
  onChange: (value: string) => void;
  /** Text that shows prior to user typing (optional) */
  placeholder?: string;
  /** Whether or not textarea is required */
  required?: boolean;
  /** Text that will be displayed next to the label when `required` is false */
  optionalLabelText?: string;
  /** Render the text area as a speech bubble (no rounded corner on the top left) */
  speechBubble?: boolean;
  /** Tab index of the element */
  tabIndex?: number;
  /** Whether to render the text area full width */
  fullWidth?: boolean;
  /** Text value of the textbox */
  value?: string;
  /** Text that shows prior to user typing with mentions enabled (optional) */
  mentionsPlaceholder?: string;
  /** Captures the ref of the text area element */
  inputRef?: React.RefObject<HTMLTextAreaElement>;
  /** Whether to display max length above the textarea */
  maxLengthAbove?: boolean;
  /** If provided, we will render this tooltip when hovering */
  tooltip?: React.ReactNode;
  /** If true, the element will autofocus when rendered */
  autoFocus?: boolean;
  /** The form element name of the component */
  name?: string;
  /** Test ID attribute on the text box */
  ["data-testid"]?: string;
}

/**
 * A TextArea component that grows with the user's input.
 *
 * Passing a function as `onMention` enables this component to support typeahead mentions using `react-mentions`. When the user types an @ symbol, `onMention` will be invoked with the text following the @ symbol.
 *
 * You can use the string in `input` to fetch user suggestions from an API, and then call `callback` with an array of matching suggestions.
 *
 * Suggestions should be provided as `UserProfile` objects. See [`UserDisplay`](index.html?selectedKind=UserDisplay) for the shape of these objects.
 */
export function TextArea({
  className,
  disabled,
  error = "",
  helperText = "",
  id,
  label,
  maxLength,
  minRows,
  onMention,
  onChange,
  placeholder,
  required,
  optionalLabelText,
  speechBubble = false,
  tabIndex,
  fullWidth = false,
  value = "",
  mentionsPlaceholder = "Mention others with @name",
  inputRef,
  maxLengthAbove = false,
  tooltip,
  minHeight,
  maxHeight,
  ...textAreaProps
}: TextAreaProps) {
  const mentionLinkRegex = RegExp(
    '<a\\s+href="/users/([0-9a-f]+)">@([^<]+)</a>',
    "gi"
  );

  const mentionsFormatted = (comment: string) =>
    comment.replace(mentionLinkRegex, "@[$2]($1)");
  const formattedValue = mentionsFormatted(value);

  const renderControl = () => {
    const classes = cx("textarea", { error: Boolean(error), speechBubble });
    const defaultMentionsPlaceholder = onMention ? mentionsPlaceholder : "";
    return (
      <MentionsInput
        className={classes}
        classNames={styles}
        id={id.toString()}
        value={formattedValue}
        placeholder={placeholder || defaultMentionsPlaceholder}
        maxLength={maxLength}
        tabIndex={tabIndex}
        rows={minRows}
        disabled={disabled || false}
        onChange={(e) => onChange(e.target.value)}
        allowSpaceInQuery={true}
        inputRef={inputRef}
        style={{ maxHeight, minHeight }}
        {...textAreaProps}
      >
        {onMention ? (
          <Mention
            trigger="@"
            className={styles["suggestion"]}
            renderSuggestion={renderSuggestion}
            displayTransform={(_id: string, display: string) => `@${display}`}
            data={handleOnMention(onMention)}
          />
        ) : (
          <Mention trigger="@" data={[]} />
        )}
      </MentionsInput>
    );
  };

  const controlWrapperProps = {
    value,
    label,
    inputId: id,
    disabled,
    helperText,
    required,
    optionalLabelText,
    fullWidth,
    error,
    charactersRemaining: value ? value.length : 0,
    maxLength,
    renderControl,
    maxLengthAbove,
    tooltip,
  };
  return (
    <div
      data-testid="textarea-container"
      className={cx(styles["container"], className)}
    >
      <ControlWrapper {...controlWrapperProps} />
    </div>
  );
}

type OnMentionFn = (search: string, callback: (results: any) => void) => void;

export function handleOnMention(onMention: OnMentionFn) {
  return (search: string, callback: Parameters<OnMentionFn>[1]) => {
    if (search.length > MAX_MENTION_SEARCH_LENGTH) {
      callback([]);
      return; // Ensure callback is called with empty array to prevent execution
    }
    onMention(search, (results) => {
      callback(mapUserProfileToMention(results));
    });
  };
}

export interface UncontrolledTextAreaProps extends TextAreaProps {
  /** Interval for debouncing the onChange event */
  debounceInterval?: number;
}

/**
 * Uncontrolled version of the TextArea component with debouncing
 */
export function UncontrolledTextArea(props: UncontrolledTextAreaProps) {
  const { debounceInterval = 100, value, onChange, ...textAreaProps } = props;
  const [internalValue, setInternalValue] = React.useState(props.value);
  const onChangeRef = React.useRef(onChange);

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      // istanbul ignore else - noop
      if (internalValue !== value) {
        onChangeRef.current(coalesce(internalValue, ""));
      }
    }, debounceInterval);

    return function cleanup() {
      clearTimeout(timeout);
    };
  }, [internalValue, debounceInterval]);

  return (
    <TextArea
      {...textAreaProps}
      value={internalValue}
      onChange={setInternalValue}
    />
  );
}

/**
 * Maps user profile array to mention format
 */
function mapUserProfileToMention(userProfileArray: any[]) {
  return userProfileArray.map((item) => ({ ...item, display: item.name }));
}

/**
 * Renders the suggestion for mentions
 */
export function renderSuggestion(
  entry: any,
  search: string,
  highlightedDisplay: React.ReactNode
) {
  return (
    <TextAreaMentionSuggestion
      id={entry.id}
      name={highlightedDisplay}
      userProfile={entry}
    />
  );
}
